import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useKycDocumentsTokenValidation } from '@/hooks/useKycDocuments';
import { useCommonTranslation } from '@/hooks/useTranslation';
import { DocumentGroup } from '@/types/kycDocuments.types';
import { AlertCircle, Loader2, ShieldX } from 'lucide-react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

interface KycDocumentsTokenValidationGuardProps {
  children: React.ReactNode;
  setDocumentGroups: (documentGroups: DocumentGroup[]) => void;
}

/**
 * KYC Documents Token Validation Guard
 * Validates KYC documents tokens before allowing access to document upload forms
 * Uses the KYC documents-specific validation endpoint
 */
export function KycDocumentsTokenValidationGuard({
  children,
  setDocumentGroups,
}: KycDocumentsTokenValidationGuardProps) {
  const { token } = useParams<{ token: string }>();
  const { t } = useCommonTranslation();

  const {
    data: tokenValidation,
    isLoading,
    error,
    refetch,
  } = useKycDocumentsTokenValidation(token || '');

  // Set document groups when token validation succeeds
  useEffect(() => {
    if (tokenValidation?.success && tokenValidation?.result?.valid) {
      setDocumentGroups(tokenValidation.result.document_groups || []);
    }
  }, [tokenValidation, setDocumentGroups]);

  // Loading state
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardContent className='flex flex-col items-center justify-center py-8'>
            <Loader2 className='h-8 w-8 animate-spin text-primary mb-4' />
            <p className='text-sm text-muted-foreground text-center'>
              {t('validation.validatingToken')}
            </p>
            <p className='text-xs text-muted-foreground text-center mt-2'>
              {t('validation.verifyingPermissions')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state or invalid token
  if (error || !tokenValidation?.success || !tokenValidation?.result?.valid) {
    const errorMessage =
      error?.message ||
      tokenValidation?.message ||
      t('errors.tokenInvalidOrExpired');

    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardHeader className='text-center'>
            <div className='flex justify-center mb-4'>
              <ShieldX className='h-12 w-12 text-destructive' />
            </div>
            <CardTitle className='text-xl'>{t('access.denied')}</CardTitle>
            <CardDescription>{t('access.tokenInvalid')}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>

            <div className='flex flex-col gap-2'>
              <Button
                variant='outline'
                onClick={() => refetch()}
                className='w-full'
              >
                {t('actions.tryAgain')}
              </Button>
              <Button
                variant='ghost'
                onClick={() => window.close()}
                className='w-full'
              >
                {t('actions.closeWindow')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid token - render children
  if (tokenValidation?.success && tokenValidation?.result?.valid) {
    return <>{children}</>;
  }

  return null;
}

export default KycDocumentsTokenValidationGuard;
